import type { RehypeNode } from './rehype-plugins';

export type ObjectLinkPhrase = {
  phrase: string;
};

const SKIP_TAGS = new Set(['a', 'code', 'pre']);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isEscapedLink(node: RehypeNode): boolean {
  const className = node.properties?.className;
  const names = Array.isArray(className)
    ? className
    : className
      ? [className]
      : [];

  return names.some((name) => String(name).includes('escaped-link'));
}

const PHRASE_EDGE = String.raw`[A-Za-z0-9_/-]`;
const PHRASE_AFTER = String.raw`(?:${PHRASE_EDGE}|\.${PHRASE_EDGE})`;

export function rehypeObjectLinks({
  links = [],
}: {
  links?: readonly ObjectLinkPhrase[];
} = {}) {
  const indexByPhrase = new Map(
    links.map((link, index) => [link.phrase.toLowerCase(), index]),
  );
  const pattern = [...links]
    .filter((link) => link.phrase.length > 0)
    .sort((left, right) => right.phrase.length - left.phrase.length)
    .map(
      (link) =>
        `(?<!${PHRASE_EDGE})${escapeRegExp(link.phrase)}(?!${PHRASE_AFTER})`,
    )
    .join('|');
  const regex = pattern ? new RegExp(pattern, 'gi') : null;

  return (tree: RehypeNode) => {
    if (!regex) return;

    const splitText = (value: string): RehypeNode[] | null => {
      regex.lastIndex = 0;
      const nodes: RehypeNode[] = [];
      let cursor = 0;
      let found = false;

      for (const match of value.matchAll(regex)) {
        const phrase = match[0];
        const index = indexByPhrase.get(phrase.toLowerCase());

        if (index === undefined || match.index === undefined) continue;

        found = true;

        if (match.index > cursor) {
          nodes.push({ type: 'text', value: value.slice(cursor, match.index) });
        }

        nodes.push({
          type: 'element',
          tagName: 'span',
          properties: {
            dataObjectLink: String(index),
          },
          children: [{ type: 'text', value: phrase }],
        });
        cursor = match.index + phrase.length;
      }

      if (!found) return null;

      if (cursor < value.length) {
        nodes.push({ type: 'text', value: value.slice(cursor) });
      }

      return nodes;
    };

    const walk = (node: RehypeNode) => {
      if (!node.children) return;

      if (node.type === 'element') {
        if (
          (node.tagName && SKIP_TAGS.has(node.tagName)) ||
          isEscapedLink(node)
        ) {
          return;
        }
      }

      const next: RehypeNode[] = [];

      for (const child of node.children) {
        if (child.type === 'text' && child.value) {
          const linked = splitText(child.value);

          if (linked) {
            next.push(...linked);
            continue;
          }
        }

        walk(child);
        next.push(child);
      }

      node.children = next;
    };

    walk(tree);
  };
}
